import {
  ComponentType,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useRouter } from "next/router";

import { PURCHASE_TYPE, PurchaseType } from "./constants";
import { GenerativeCreditsPurchasePopup } from "./GenerativeCreditsPurchasePopup";
import { SubscriptionPurchasePopup } from "./SubscriptionPurchasePopup";
import { ApiSubscriptionPurchasePopup } from "./ApiSubscriptionPurchasePopup";

type GenerativeCreditsPurchaseDetails = {
  type: typeof PURCHASE_TYPE.ADD_ON_GENERATIVE_CREDITS;
};

type SubscriptionPurchaseDetails = {
  type: typeof PURCHASE_TYPE.SUBSCRIPTION;
  plan: "personal" | "team";
  period: "monthly" | "yearly";
  seats?: number;
};

type ApiSubscriptionPurchaseDetails = {
  type: typeof PURCHASE_TYPE.API_SUBSCRIPTION;
  plan: "standard" | "premium";
  period: "monthly" | "yearly";
};

type PurchaseDetails =
  | GenerativeCreditsPurchaseDetails
  | SubscriptionPurchaseDetails
  | ApiSubscriptionPurchaseDetails;

type PurchasePopupContextValue = {
  isOpened: boolean;
  openedPurchaseType?: PurchaseType;
  openedPurchaseDetails?: PurchaseDetails;
  open?: (purchaseDetails: PurchaseDetails) => void;
  close?: () => void;
};

export const PurchasePopupContext = createContext<PurchasePopupContextValue>({
  isOpened: false,
});

export const PurchasePopupProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const router = useRouter();

  const [openedPurchaseDetails, setOpenedPurchaseDetails] =
    useState<PurchaseDetails>();

  useEffect(() => {
    setOpenedPurchaseDetails(undefined);
  }, [router.asPath]);

  const isOpened = !!openedPurchaseDetails;
  const openedPurchaseType = openedPurchaseDetails?.type;

  const isOpenedAddOnGenerativeCreditsPopup =
    openedPurchaseType === PURCHASE_TYPE.ADD_ON_GENERATIVE_CREDITS;

  const isOpenedSubscriptionPopup =
    openedPurchaseType === PURCHASE_TYPE.SUBSCRIPTION;

  const isOpenedApiSubscriptionPopup =
    openedPurchaseType === PURCHASE_TYPE.API_SUBSCRIPTION;

  const close = useCallback(() => {
    setOpenedPurchaseDetails(undefined);
  }, []);

  const open = useCallback((purchaseDetails: PurchaseDetails) => {
    setOpenedPurchaseDetails(purchaseDetails);
  }, []);

  return (
    <PurchasePopupContext.Provider
      value={{
        isOpened,
        openedPurchaseType,
        openedPurchaseDetails,
        close,
        open,
      }}
    >
      {children}

      {/* 
        생성형 크레딧 결제 모달
      */}
      <GenerativeCreditsPurchasePopup
        isOpened={isOpenedAddOnGenerativeCreditsPopup}
        onClose={close}
      />

      {/* 
        구독 결제 모달
      */}
      <SubscriptionPurchasePopup
        isOpened={isOpenedSubscriptionPopup}
        onClose={close}
        plan={
          openedPurchaseDetails?.type === PURCHASE_TYPE.SUBSCRIPTION
            ? openedPurchaseDetails.plan
            : undefined
        }
        period={
          openedPurchaseDetails && "period" in openedPurchaseDetails
            ? openedPurchaseDetails.period
            : undefined
        }
        seats={
          openedPurchaseDetails && "seats" in openedPurchaseDetails
            ? openedPurchaseDetails.seats
            : undefined
        }
      />

      {/* 
        API 구독 결제 모달
      */}
      <ApiSubscriptionPurchasePopup
        isOpened={isOpenedApiSubscriptionPopup}
        onClose={close}
        plan={
          openedPurchaseDetails?.type === PURCHASE_TYPE.API_SUBSCRIPTION
            ? openedPurchaseDetails.plan
            : undefined
        }
        period={
          openedPurchaseDetails?.type === PURCHASE_TYPE.API_SUBSCRIPTION
            ? openedPurchaseDetails.period
            : undefined
        }
      />

      {/* 
        결제 모달 종류별로 추가...
      */}
    </PurchasePopupContext.Provider>
  );
};

export const usePurchasePopup = () => {
  const { isOpened, openedPurchaseType, openedPurchaseDetails, close, open } =
    useContext(PurchasePopupContext);

  return {
    isOpened,
    openedPurchaseType,
    openedPurchaseDetails,
    close,
    open,
  };
};

export const withPurchasePopupProvider = (Component: ComponentType) => {
  return (props: any) => (
    <PurchasePopupProvider>
      <Component {...props} />
    </PurchasePopupProvider>
  );
};
